import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { useCustomer } from '../../contexts/CustomerContext';
import PendingChangesReview from '../../components/admin/PendingChangesReview';
import { fetchPendingChanges, approveChange, rejectChange, confirmBackendWork, approvePolicyChange, rejectPolicyChange } from '../../api/equipmentApi';

const AdminEquipmentReview = () => {
    const user = useAuthUser();
    const { currentCustomer } = useCustomer();
    const [pendingChanges, setPendingChanges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState('wards');
    const [availableCustomers] = useState([
        { id: 'wards', name: "Ward's Rental" }
    ]);

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        content: false
    });

    useEffect(() => {
        const timer1 = setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100);

        const timer2 = setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
        }, 300);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, []);

    // Fetch pending changes for the selected customer
    const fetchAllPendingChanges = async (customerId = selectedCustomerId) => {
        try {
            setIsLoading(true);
            setError(null);
            
            // console.log(`Fetching all pending changes for customer: ${customerId}`);
            
            // Call the main pending changes endpoint that returns both modifications and creations
            const response = await fetchPendingChanges(customerId);
            
            // console.log('Pending changes response:', response);
            
            // Handle the response structure from the main endpoint
            let allChanges = [];
            if (response) {
                if (response.data && response.data.all && Array.isArray(response.data.all)) {
                    // New structure with all/creations/modifications
                    allChanges = response.data.all;
                    // console.log('Using response.data.all:', allChanges);
                } else if (Array.isArray(response)) {
                    // Direct array response
                    allChanges = response;
                    // console.log('Using direct array response:', allChanges);
                } else if (response.data && Array.isArray(response.data)) {
                    // Legacy structure
                    allChanges = response.data;
                    // console.log('Using response.data:', allChanges);
                } else if (response.changes && Array.isArray(response.changes)) {
                    // Alternative structure
                    allChanges = response.changes;
                    // console.log('Using response.changes:', allChanges);
                } else {
                    console.warn('Unexpected response structure:', response);
                    allChanges = [];
                }
            }
            
            // Filter out policy changes - those are handled in PolicyReview page
            allChanges = allChanges.filter(change => change.changeType !== 'policy');
            
            // console.log('Processed all changes data:', allChanges);
            setPendingChanges(allChanges);
        } catch (err) {
            console.error('Error fetching pending changes:', err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch pending changes');
            setPendingChanges([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle customer selection change
    const handleCustomerChange = (customerId) => {
        setSelectedCustomerId(customerId);
        fetchAllPendingChanges(customerId);
    };

    // Handle approval of a change
    const handleApprove = async (changeId, changeData) => {
        try {
            // console.log(`Approving change: ${changeId}`);
            // Use policy-specific functions if changeType is 'policy'
            if (changeData?.changeType === 'policy') {
                await approvePolicyChange(selectedCustomerId, changeId);
            } else {
                await approveChange(selectedCustomerId, changeId);
            }
            
            // Refresh the pending changes list
            await fetchAllPendingChanges();
            
            // Show success message (you can add a toast notification here)
            // console.log('Change approved successfully');
        } catch (err) {
            console.error('Error approving change:', err);
            setError(err.response?.data?.message || err.message || 'Failed to approve change');
        }
    };

    // Handle rejection of a change
    const handleReject = async (changeId, rejectionReason, changeData) => {
        try {
            // console.log(`Rejecting change: ${changeId} with reason: ${rejectionReason}`);
            // Use policy-specific functions if changeType is 'policy'
            if (changeData?.changeType === 'policy') {
                await rejectPolicyChange(selectedCustomerId, changeId, rejectionReason);
            } else {
                await rejectChange(selectedCustomerId, changeId, rejectionReason);
            }
            
            // Refresh the pending changes list
            await fetchAllPendingChanges();
            
            // Show success message (you can add a toast notification here)
            // console.log('Change rejected successfully');
        } catch (err) {
            console.error('Error rejecting change:', err);
            setError(err.response?.data?.message || err.message || 'Failed to reject change');
        }
    };

    // Handle confirmation that backend work is complete
    const handleConfirmBackendWork = async () => {
        try {
            // console.log('Confirming backend work completion');
            await confirmBackendWork(selectedCustomerId);
            
            // Refresh the pending changes list (should be empty now)
            await fetchAllPendingChanges();
            
            // Show success message (you can add a toast notification here)
            // console.log('Backend work confirmed and changes cleared');
        } catch (err) {
            console.error('Error confirming backend work:', err);
            setError(err.response?.data?.message || err.message || 'Failed to confirm backend work');
        }
    };

    // Load pending changes on component mount
    useEffect(() => {
        fetchAllPendingChanges();
    }, []);

    if (!user?.isHeliosAdmin) {
        return (
            <div className="responsive-page-layout scrollable">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <Icon icon="mdi:lock" className="text-4xl text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">Access Restricted</p>
                        <p className="text-gray-500 text-sm">Equipment review is only available for Helios administrators</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="responsive-page-layout scrollable">
            {/* Header */}
            <div className={`flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center w-full transition-all duration-700 ease-out-in ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-4'
            }`}>
                <div className="header-textbox">
                    <Icon icon="material-symbols:rate-review-rounded" className="header-icon" />
                    Inventory Changes
                </div>
                <div className="flex flex-row gap-2 items-center">
                    {/* Customer Selector */}
                    <select
                        value={selectedCustomerId}
                        onChange={(e) => handleCustomerChange(e.target.value)}
                        className="button-container p-2 rounded-md text-sm text-font-primary border-none focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {availableCustomers.map(customer => (
                            <option key={customer.id} value={customer.id}>
                                {customer.name}
                            </option>
                        ))}
                    </select>
                    {/* Refresh Button */}
                    <button
                        onClick={() => fetchAllPendingChanges()}
                        disabled={isLoading}
                        className="flex flex-row gap-2 items-center text-font-primary button-container p-2 rounded-md text-sm group hover:bg-primary transition-colors duration-300 disabled:opacity-50"
                    >
                        <Icon 
                            icon="material-symbols:refresh" 
                            className={`text-xl text-primary group-hover:text-background ${isLoading ? 'animate-spin' : ''}`} 
                        />
                        <span className="text-sm font-medium group-hover:text-background">Refresh</span>
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className={`flex flex-col gap-6 h-full w-full transition-all duration-1000 ease-out-in ${
                animationStates.content
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <Icon icon="mdi:alert-circle" className="text-red-500 text-xl" />
                            <span className="text-red-700 font-medium">Error</span>
                        </div>
                        <p className="text-red-600 text-sm mt-1">{error}</p>
                    </div>
                )}

                <PendingChangesReview
                    pendingChanges={pendingChanges}
                    isLoading={isLoading}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onConfirmBackendWork={handleConfirmBackendWork}
                    selectedCustomer={availableCustomers.find(c => c.id === selectedCustomerId)}
                />
            </div>
        </div>
    );
};

export default AdminEquipmentReview;
